import { useState, useRef } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, resolveImg, formatApiError } from "@/lib/api";

// Gallery editor: list of { url, caption } items with per-photo caption inputs.
export default function GalleryEditor({ value = [], onChange, label = "Field gallery (photos with captions)" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const items = Array.isArray(value) ? value : [];

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of list) uploaded.push({ url: await uploadImage(f), caption: "" });
      onChange([...items, ...uploaded]);
      toast.success(uploaded.length > 1 ? "Photos added to gallery" : "Photo added to gallery");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setCaption = (i, caption) => onChange(items.map((it, idx) => (idx === i ? { ...it, caption } : it)));
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div data-testid="gallery-editor">
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">{label}</span>
      <div className="mt-1 space-y-2">
        {items.map((item, i) => (
          <div key={`${item.url}-${i}`} data-testid="gallery-editor-item" className="flex items-center gap-3 border border-ptf-primary/15 bg-white p-2">
            <img src={resolveImg(item.url)} alt="" className="h-16 w-24 shrink-0 object-cover" />
            <input
              value={item.caption || ""}
              onChange={(e) => setCaption(i, e.target.value)}
              placeholder="Caption from the field (optional)"
              data-testid="gallery-caption-input"
              className="min-w-0 flex-1 border border-ptf-primary/20 px-3 py-2 text-sm outline-none focus:border-ptf-secondary transition-colors"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid="gallery-remove-item"
              className="grid h-8 w-8 shrink-0 place-items-center border border-ptf-primary/15 hover:bg-red-50"
            >
              <X className="h-4 w-4 text-red-600" />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid="gallery-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-dashed border-ptf-primary/30 px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-ptf-secondary hover:bg-ptf-primary/5 transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Add photos
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
